import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

export type GuardOperationalEventType = 'Notice' | 'Incident';
export type GuardOperationalEventPriority = 'Low' | 'Medium' | 'High';
export type GuardOperationalEventStatus = 'Open' | 'InProgress' | 'Resolved' | 'Cancelled';
export type GuardFieldOperationsView = 'incidents' | 'maintenance' | null;
export type FieldOperationsAccess = 'guard' | 'manager';

export interface GuardFieldPoint {
  x: number;
  y: number;
  srid: number;
}

export type GuardFieldElementType = 'Canal' | 'SecondaryCanal' | 'Take' | 'Parcel';

export interface GuardFieldElement {
  type: GuardFieldElementType;
  id: number;
  label: string;
}

export interface GuardOperationalEvent {
  id: number;
  type: GuardOperationalEventType;
  elementType?: string | null;
  elementId?: number | null;
  title: string;
  description?: string | null;
  priority: GuardOperationalEventPriority;
  status: GuardOperationalEventStatus;
  createdAt: string;
  geometry?: GuardFieldPoint | null;
}

export interface GuardMaintenanceExecution {
  id: number;
  durationMinutes: number;
  backpackQuantity: number;
  recordedAt: string;
  effectiveAt: string;
}

export interface GuardMaintenanceRecord {
  id: number;
  guardId: number;
  elementType: string;
  elementId: number;
  isMaintenanceCompleted: boolean;
  observations?: string | null;
  imageUrl?: string | null;
  inspectionRecordedAt: string;
  inspectionEffectiveAt: string;
  geometry: GuardFieldPoint;
  execution?: GuardMaintenanceExecution | null;
}

interface FieldOperationsPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface CreateGuardOperationalEvent {
  type: GuardOperationalEventType;
  title: string;
  description?: string;
  priority: GuardOperationalEventPriority;
  elementType?: string;
  elementId?: number;
  geometry: Pick<GuardFieldPoint, 'x' | 'y'>;
}

export interface CreateGuardMaintenanceInspection {
  elementType?: string;
  elementId?: number;
  isMaintenanceCompleted: boolean;
  observations?: string;
  image?: {
    fileName: string;
    contentType: string;
    contentBase64: string;
  };
  geometry: Pick<GuardFieldPoint, 'x' | 'y'>;
}

export interface CreateGuardMaintenanceExecution {
  durationMinutes: number;
  backpackQuantity: number;
}

export interface OperationDateCorrection {
  id: number;
  targetType: 'MaintenanceInspection' | 'MaintenanceExecution' | 'WaterIntakeStatus';
  targetId: number;
  incidentId: number;
  previousEffectiveAt: string;
  correctedEffectiveAt: string;
  correctedAt: string;
  managerId: number;
}

export type FieldOperationResult<T> =
  | { kind: 'ready'; value: T }
  | { kind: 'unauthorized' }
  | { kind: 'invalid'; message: string }
  | { kind: 'unavailable' };

@Injectable({ providedIn: 'root' })
export class GuardFieldOperationsService {
  async getOperationalEvents(
    headers: Record<string, string>,
    access: FieldOperationsAccess = 'guard'
  ): Promise<FieldOperationResult<FieldOperationsPage<GuardOperationalEvent>>> {
    return this.request<FieldOperationsPage<GuardOperationalEvent>>(
      `${this.getOperationsBaseUrl(access)}/operational-events?page=1&pageSize=100`,
      { headers }
    );
  }

  async createOperationalEvent(
    event: CreateGuardOperationalEvent,
    headers: Record<string, string>
  ): Promise<FieldOperationResult<GuardOperationalEvent>> {
    return this.request<GuardOperationalEvent>(`${environment.apiWmsUrl}/guards/operational-events`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(event)
    });
  }

  async getMaintenanceRecords(
    headers: Record<string, string>,
    access: FieldOperationsAccess = 'guard'
  ): Promise<FieldOperationResult<FieldOperationsPage<GuardMaintenanceRecord>>> {
    return this.request<FieldOperationsPage<GuardMaintenanceRecord>>(
      `${this.getOperationsBaseUrl(access)}/maintenance-records?page=1&pageSize=100`,
      { headers }
    );
  }

  async updateOperationalEventStatus(
    eventId: number,
    status: GuardOperationalEventStatus,
    headers: Record<string, string>
  ): Promise<FieldOperationResult<GuardOperationalEvent>> {
    return this.request<GuardOperationalEvent>(
      `${environment.apiBaseUrl}/manager/operational-events/${eventId}/status`,
      {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      }
    );
  }

  async createMaintenanceInspection(
    inspection: CreateGuardMaintenanceInspection,
    headers: Record<string, string>
  ): Promise<FieldOperationResult<GuardMaintenanceRecord>> {
    return this.request<GuardMaintenanceRecord>(`${environment.apiWmsUrl}/guards/maintenance-records`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(inspection)
    });
  }

  async createMaintenanceExecution(
    recordId: number,
    execution: CreateGuardMaintenanceExecution,
    headers: Record<string, string>
  ): Promise<FieldOperationResult<GuardMaintenanceRecord>> {
    return this.request<GuardMaintenanceRecord>(
      `${environment.apiWmsUrl}/guards/maintenance-records/${recordId}/execution`,
      {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(execution)
      }
    );
  }

  async createMaintenanceDateCorrectionIncident(
    recordId: number,
    title: string,
    description: string,
    priority: GuardOperationalEventPriority,
    geometry: Pick<GuardFieldPoint, 'x' | 'y'>,
    headers: Record<string, string>,
    executionId?: number
  ): Promise<FieldOperationResult<GuardOperationalEvent>> {
    return this.createOperationalEvent({
      type: 'Incident',
      title,
      description,
      priority,
      elementType: executionId ? 'MaintenanceExecution' : 'MaintenanceInspection',
      elementId: executionId ?? recordId,
      geometry
    }, headers);
  }

  async createWaterIntakeDateCorrectionIncident(
    operationId: number,
    title: string,
    description: string,
    priority: GuardOperationalEventPriority,
    geometry: Pick<GuardFieldPoint, 'x' | 'y'>,
    headers: Record<string, string>
  ): Promise<FieldOperationResult<GuardOperationalEvent>> {
    return this.createOperationalEvent({
      type: 'Incident',
      title,
      description,
      priority,
      elementType: 'WaterIntakeStatus',
      elementId: operationId,
      geometry
    }, headers);
  }

  async applyOperationDateCorrection(
    targetType: OperationDateCorrection['targetType'],
    targetId: number,
    incidentId: number,
    correctedEffectiveAt: string,
    headers: Record<string, string>
  ): Promise<FieldOperationResult<OperationDateCorrection>> {
    return this.request<OperationDateCorrection>(
      `${environment.apiBaseUrl}/manager/operation-date-corrections`,
      {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetType, targetId, incidentId, correctedEffectiveAt })
      }
    );
  }

  private async request<T>(url: string, init: RequestInit): Promise<FieldOperationResult<T>> {
    try {
      const response = await fetch(url, init);

      if (response.status === 401 || response.status === 403) {
        return { kind: 'unauthorized' };
      }

      if (response.status === 400 || response.status === 404 || response.status === 409) {
        const payload = await response.json().catch(() => undefined) as { error?: string } | undefined;
        return {
          kind: 'invalid',
          message: payload?.error || 'Revisa los datos introducidos.'
        };
      }

      if (!response.ok) {
        return { kind: 'unavailable' };
      }

      return { kind: 'ready', value: await response.json() as T };
    } catch {
      return { kind: 'unavailable' };
    }
  }

  private getOperationsBaseUrl(access: FieldOperationsAccess): string {
    return access === 'manager'
      ? `${environment.apiBaseUrl}/manager`
      : `${environment.apiWmsUrl}/guards`;
  }
}
